import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, CircularProgress, Divider } from '@material-ui/core'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { COLUMN_CATALOG_MAP } from '../../lib/constants'
import { effectiveFormat } from '../../lib/effectiveFormat'
import { usePortfolioData } from '../PortfolioDataProvider'
import MeasureDropdown from '../MeasureDropdown'
import MeasurePickerDialog from '../MeasurePickerDialog'
import { buildMeasureSections } from '../buildMeasureSections'
import { buildLevelRequest, buildRowRequest } from './allocation/buildAllocationRequest'
import { useAllocationQuery } from './allocation/useAllocationQuery'
import { computeSlices } from './allocation/computeSlices'
import { loadChartType, saveChartType } from './allocation/chartTypePreference'
import ChartTypeToggle from './allocation/ChartTypeToggle'
import AllocationDonut from './allocation/AllocationDonut'
import AllocationBarChart from './allocation/AllocationBarChart'
import AllocationTreeMap from './allocation/AllocationTreeMap'
import AllocationDetailTable from './allocation/AllocationDetailTable'

// Chart-type dispatch. Keep keys in sync with CHART_TYPES in
// chartTypePreference.js -- the preference module validates incoming
// values against that list, so a typo here would silently fall back
// to donut.
const CHART_COMPONENTS = {
  donut: AllocationDonut,
  bar: AllocationBarChart,
  treemap: AllocationTreeMap
}

const useStyles = makeStyles(() => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 14
  },
  loading: {
    flex: 1,
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  error: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 24px',
    textAlign: 'center',
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13
  },
  empty: {
    flex: 1,
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  },
  warningChip: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    padding: '4px 8px',
    borderRadius: 4,
    background: 'var(--color-warning-050, #fff8e1)',
    color: 'var(--color-warning-700, #b26a00)',
    border: '1px solid var(--color-warning-200, #ffe082)'
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4
  }
}))

// Identifies columns that make sense in a pie chart. v1 is intentionally
// narrow -- only material currency columns. Derived currency columns
// (e.g. mvChange) are conceptually deltas, not magnitudes, and window
// columns are percentages-of-something; allowing them in v1 would
// invite confusing charts. Future flag on the catalog entry could
// loosen this without changing the picker plumbing.
function isPieEligible (col) {
  const catalog = COLUMN_CATALOG_MAP[col.columnType]
  if (!catalog) return false
  if (catalog.kind && catalog.kind !== 'material') return false
  return catalog.measureKind === 'currency'
}

// Catalog-entry variant for MeasurePickerDialog. Same rule, just
// pre-resolved to the catalog entry (the dialog already filters out
// non-material kinds; we only need to gate by measureKind).
function isPieEligibleCatalog (catalogEntry) {
  return catalogEntry?.measureKind === 'currency'
}

// Join column instances with columnDefinitions for the picker.
// Catalog-injected instances aren't in columnDefinitions, so fall back
// to the catalog entry's own format / measureKind / header label.
function deriveMeasureOptions (columns, columnDefinitions) {
  const defsById = new Map((columnDefinitions || []).map(d => [d.accessorKey, d]))
  const out = []
  for (const col of columns || []) {
    if (!isPieEligible(col)) continue
    const def = defsById.get(col.id)
    const catalog = COLUMN_CATALOG_MAP[col.columnType]
    const fallbackLabel = catalog?.label || col.columnType
    out.push({
      id: col.id,
      label: def?.header || fallbackLabel,
      format: def?.format ?? catalog?.format,
      measureKind: def?.measureKind ?? catalog?.measureKind
    })
  }
  return out
}

export default function AllocationTool ({ payload, compact, onPayloadChange }) {
  const classes = useStyles()
  const { userId } = useAppContext()
  const {
    config,
    asOfDate,
    columnDefinitions,
    prefs,
    clientId,
    entity,
    entityIds,
    selectedSubject
  } = usePortfolioData()

  // chartType: payload.chartType wins (per-instance persistence). When
  // unset, fall back to the user's global localStorage pref. User
  // changes BOTH write to the global pref (so new instances inherit
  // their last-used type) AND patch the instance's payload so this
  // specific tile remembers the choice across reloads. When the
  // instance can't persist (no onPayloadChange -- transient tile),
  // local state holds the value for this session.
  const [localChartType, setLocalChartType] = useState(() => payload?.chartType || loadChartType(userId))
  const chartType = payload?.chartType || localChartType
  const handleChartTypeChange = useCallback((next) => {
    setLocalChartType(next)
    saveChartType(userId, next)
    if (onPayloadChange) onPayloadChange({ chartType: next })
  }, [userId, onPayloadChange])

  // extraMeasures: measures the user picked via MeasurePickerDialog
  // that aren't in the user's view. Persisted in payload so the
  // dropdown remembers them across reloads. Local state mirrors
  // payload for the transient-tile case where there's no persistence.
  const [localExtras, setLocalExtras] = useState(() => payload?.extraMeasures || [])
  const extraMeasures = payload?.extraMeasures || localExtras

  // Union the view's columns with catalog-injected preset + extras
  // so the Orion request includes the calcs needed by the chart even
  // when the user's table doesn't contain them.
  const chartColumns = useMemo(() => {
    const existing = config?.columns || []
    const preset = [payload?.measureColumnInstance, ...extraMeasures].filter(Boolean)
    const merged = [...existing]
    for (const c of preset) {
      if (!merged.some(x => x.id === c.id)) merged.push(c)
    }
    return merged
  }, [config?.columns, payload?.measureColumnInstance, extraMeasures])

  const requestConfig = useMemo(
    () => ({ ...config, columns: chartColumns }),
    [config, chartColumns]
  )

  // Subject wins over the catalog payload's default scope:
  //   row    -> allocate composition of that row's children
  //   level  -> allocate by that grouping depth across the household
  //   null   -> use the payload's preset scope (catalog default)
  const built = useMemo(() => {
    if (!payload || !requestConfig || !asOfDate || !entity || !entityIds) return null
    if (selectedSubject?.kind === 'row') {
      return buildRowRequest({ config: requestConfig, asOfDate, entity, entityIds, row: selectedSubject.row })
    }
    if (selectedSubject?.kind === 'level') {
      return buildLevelRequest({ config: requestConfig, asOfDate, entity, entityIds, depth: selectedSubject.depth })
    }
    if (payload.scope === 'level') {
      return buildLevelRequest({ config: requestConfig, asOfDate, entity, entityIds, depth: payload.depth })
    }
    if (payload.scope === 'row') {
      return buildRowRequest({ config: requestConfig, asOfDate, entity, entityIds, row: payload.row })
    }
    return null
  }, [payload, requestConfig, asOfDate, entity, entityIds, selectedSubject])

  const request = built?.request || null
  const unwrap = built?.unwrap || null

  const globalDateConfig = useMemo(() => ({
    dateMode: config.dateMode,
    quickDate: config.quickDate,
    startDate: config.startDate,
    endDate: config.endDate
  }), [config.dateMode, config.quickDate, config.startDate, config.endDate])

  const { items, isFetching, error } = useAllocationQuery({
    request,
    clientId,
    columnInstances: chartColumns,
    globalDateConfig,
    entity,
    entityIds,
    unwrap
  })

  // Picker includes the catalog-injected column so the user can
  // toggle back to it after exploring other measures.
  const measureOptions = useMemo(
    () => deriveMeasureOptions(chartColumns, columnDefinitions),
    [chartColumns, columnDefinitions]
  )
  const presetMeasureId = payload?.measureColumnInstance?.id || null
  const defaultMeasureId = (presetMeasureId && measureOptions.some(o => o.id === presetMeasureId))
    ? presetMeasureId
    : (measureOptions[0]?.id || null)
  const [measureId, setMeasureId] = useState(defaultMeasureId)

  // Snap measureId to a valid option during render rather than via a
  // post-paint effect. If the config changes and the previously-
  // selected measure goes away (or there was no selection), fall back
  // to the new first eligible. Render-phase setState bails out via
  // Object.is when the chosen value already matches state, so the
  // steady-state case is free; the one-off correction is a single
  // re-render with no wasted commit.
  //
  // See React docs "Adjusting some state when a prop changes":
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (measureOptions.length > 0) {
    const stillValid = measureId && measureOptions.some(o => o.id === measureId)
    if (!stillValid && defaultMeasureId !== measureId) {
      setMeasureId(defaultMeasureId)
    }
  }

  const measureMeta = measureOptions.find(o => o.id === measureId) || null

  // User-driven measure change. Persists the picked instance into the
  // payload so the tile reloads with the same measure. We look up the
  // full column instance from chartColumns since the picker only
  // gives us an id.
  const handleMeasureChange = useCallback((nextId) => {
    setMeasureId(nextId)
    if (!onPayloadChange) return
    const nextInstance = chartColumns.find(c => c.id === nextId) || null
    onPayloadChange({ measureColumnInstance: nextInstance })
  }, [onPayloadChange, chartColumns])

  // Sectioned options for the measure dropdown:
  //   "In this view" -> the user's table columns (config.columns)
  //   "Suggested"    -> catalog-injected measureColumnInstance
  //   "Added"        -> measures the user picked via the dialog
  const measureSections = useMemo(() => {
    const viewIds = (config?.columns || []).map(c => c.id)
    const suggestedIds = [payload?.measureColumnInstance?.id].filter(Boolean)
    const userIds = extraMeasures.map(c => c.id)
    const opts = measureOptions.map(o => ({ id: o.id, label: o.label }))
    return buildMeasureSections({ measureOptions: opts, viewIds, suggestedIds, userIds })
  }, [measureOptions, config?.columns, payload?.measureColumnInstance, extraMeasures])

  // Picker dialog state. On confirm: add to extras (so it stays
  // in the dropdown for swapping), persist into payload, and make
  // it the active measure.
  const [pickerOpen, setPickerOpen] = useState(false)
  const handleMeasurePicked = useCallback((instance) => {
    if (!instance) return
    const nextExtras = extraMeasures.some(c => c.id === instance.id)
      ? extraMeasures
      : [...extraMeasures, instance]
    setLocalExtras(nextExtras)
    setMeasureId(instance.id)
    if (onPayloadChange) {
      onPayloadChange({
        extraMeasures: nextExtras,
        measureColumnInstance: instance
      })
    }
  }, [extraMeasures, onPayloadChange])
  const valueFormat = useMemo(
    () => measureMeta ? effectiveFormat(measureMeta.format, measureMeta.measureKind, prefs) : null,
    [measureMeta, prefs]
  )

  // -- slice derivation --
  const slicing = useMemo(
    () => computeSlices(items, measureId),
    [items, measureId]
  )
  const { slices, totalRaw, hasNegatives, skippedZero } = slicing

  // -- render --
  // The toolbar must ALWAYS render so the user can swap measures /
  // chart types even when the current selection produced no chart
  // (no measures eligible, no grouping, request error, zero slices,
  // etc.). Putting empty / loading / error inside the body keeps the
  // picker reachable -- previously these states early-returned and
  // the user got stuck on whatever measure produced them.
  let chartContent = null
  if (measureOptions.length === 0) {
    chartContent = (
      <div className={classes.empty}>
        Add a currency column to your view to chart its allocation.
      </div>
    )
  } else if (!request) {
    chartContent = (
      <div className={classes.empty}>
        Add a grouping to your view to chart allocation.
      </div>
    )
  } else if (error) {
    chartContent = (
      <div className={classes.error}>
        <span>Could not load allocation.</span>
        <span>{error?.message || 'Unknown error'}</span>
      </div>
    )
  } else if (isFetching && slices.length === 0) {
    chartContent = (
      <div className={classes.loading}>
        <CircularProgress size={18} />
        <span>Loading allocation…</span>
      </div>
    )
  } else if (slices.length === 0) {
    chartContent = (
      <div className={classes.empty}>
        No values to chart for this measure.
      </div>
    )
  } else {
    chartContent = (
      <>
        {hasNegatives && (
          <div className={classes.warningChip}>
            {compact
              ? 'Wedge sizes show magnitude only. Hover for the actual value.'
              : 'Wedge sizes show magnitude only. Actual values (including negatives) are in the table below.'}
          </div>
        )}
        {skippedZero > 0 && !hasNegatives && (
          <div className={classes.warningChip}>
            {compact
              ? `${skippedZero} row${skippedZero === 1 ? '' : 's'} omitted (no value).`
              : `${skippedZero} row${skippedZero === 1 ? '' : 's'} omitted (no value). Listed in the table below.`}
          </div>
        )}
        {React.createElement(CHART_COMPONENTS[chartType] || AllocationDonut, {
          slices,
          valueFormat,
          height: 340
        })}
        {!compact && (
          <AllocationDetailTable
            slices={slices}
            items={items}
            measureId={measureId}
            totalRaw={totalRaw}
            valueFormat={valueFormat}
          />
        )}
      </>
    )
  }

  return (
    <div className={classes.body}>
      <div className={classes.toolbar}>
        <ChartTypeToggle value={chartType} onChange={handleChartTypeChange} />
        <Divider orientation='vertical' flexItem style={{ margin: '0 6px' }} />
        <MeasureDropdown
          sections={measureSections}
          currentId={measureId}
          triggerLabel={measureMeta?.label}
          triggerAriaLabel='Choose allocation measure'
          onChange={handleMeasureChange}
          onMore={() => setPickerOpen(true)}
        />
      </div>
      <MeasurePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handleMeasurePicked}
        title='Pick a measure to allocate by'
        isEligible={isPieEligibleCatalog}
      />
      {chartContent}
    </div>
  )
}

AllocationTool.propTypes = {
  compact: PropTypes.bool,
  onPayloadChange: PropTypes.func,
  payload: PropTypes.shape({
    scope: PropTypes.oneOf(['level', 'row']).isRequired,
    depth: PropTypes.number,
    groupingLabel: PropTypes.string,
    row: PropTypes.object,
    measureColumnInstance: PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnType: PropTypes.string
    }),
    extraMeasures: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      columnType: PropTypes.string
    })),
    chartType: PropTypes.oneOf(['donut', 'bar', 'treemap'])
  })
}
